import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ClipboardList, Wallet, Users2, FileCheck } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/people/StatusBadge';
import type { ChildSummary } from '../components/me/types';

export const Route = createFileRoute('/_authed/me/children')({
  component: ChildrenPage,
});

interface ChildrenResponse {
  items: ChildSummary[];
}

function ChildrenPage() {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const isParent = role === 'parent';

  const childrenQuery = useQuery({
    queryKey: ['me', 'children'] as const,
    queryFn: ({ signal }) =>
      apiRequest<ChildrenResponse>('/api/me/children', { signal }),
    retry: false,
    enabled: isParent,
  });

  if (role && !isParent) {
    return (
      <ListShell title="My children" description="Parent self-service.">
        <EmptyState
          title="This page is for parents"
          description="Use the dashboard for your role."
          action={
            <Link to="/dashboard">
              <Button variant="secondary">Back to dashboard</Button>
            </Link>
          }
        />
      </ListShell>
    );
  }

  const isMissingEndpoint =
    childrenQuery.error instanceof ApiError && childrenQuery.error.status === 404;

  return (
    <ListShell
      title="My children"
      description="A quick view of each child's attendance and outstanding fees."
    >
      {isMissingEndpoint ? (
        <ErrorState
          title="Parent self-service is coming soon"
          description="The /api/me/children endpoint is not yet available on this environment."
        />
      ) : childrenQuery.isError ? (
        <ErrorState
          title="Could not load your children"
          description={
            childrenQuery.error instanceof ApiError
              ? childrenQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => childrenQuery.refetch()}
        />
      ) : childrenQuery.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-44 w-full" />
        </div>
      ) : (childrenQuery.data?.items ?? []).length === 0 ? (
        <EmptyState
          title="No children linked to your household"
          description="If you believe this is wrong, please contact the academy office."
          icon={<Users2 size={32} aria-hidden="true" />}
        />
      ) : (
        <ul role="list" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(childrenQuery.data?.items ?? []).map((child) => (
            <ChildCard key={child.id} child={child} />
          ))}
        </ul>
      )}
    </ListShell>
  );
}

function ChildCard({ child }: { child: ChildSummary }) {
  return (
    <li className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] overflow-hidden">
      <header className="border-b border-[color:var(--color-border)] px-4 py-3 bg-[color:var(--color-surface)]">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-base font-semibold truncate text-[color:var(--color-text)]">
            {child.name}
          </h3>
          <StatusBadge status={child.status} />
        </div>
        <p className="mt-1 font-mono text-xs text-[color:var(--color-text-muted)]">
          {child.admissionNumber}
        </p>
      </header>

      <div className="grid grid-cols-2 gap-3 px-4 py-4">
        <Stat
          label="Attendance (30d)"
          value={`${child.attendancePercent30d}%`}
          tone={
            child.attendancePercent30d >= 80
              ? 'success'
              : child.attendancePercent30d >= 50
                ? 'warning'
                : 'danger'
          }
        />
        <Stat
          label="Unpaid fees"
          value={String(child.unpaidFeesCount)}
          tone={child.unpaidFeesCount === 0 ? 'success' : 'warning'}
        />
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-y-2 border-t border-[color:var(--color-border)] px-4 py-3 bg-[color:var(--color-bg)]">
        <Link
          to="/me/attendance"
          search={{ studentId: child.id }}
          className="inline-flex items-center gap-1 text-sm font-medium text-gold-700 hover:underline"
        >
          <ClipboardList size={14} aria-hidden="true" />
          Attendance
          <ChevronRight size={14} aria-hidden="true" />
        </Link>
        <Link
          to="/me/children/$studentId/marks"
          params={{ studentId: child.id }}
          className="inline-flex items-center gap-1 text-sm font-medium text-gold-700 hover:underline"
        >
          <FileCheck size={14} aria-hidden="true" />
          Marks
          <ChevronRight size={14} aria-hidden="true" />
        </Link>
        <Link
          to="/me/fees"
          className="inline-flex items-center gap-1 text-sm font-medium text-gold-700 hover:underline"
        >
          <Wallet size={14} aria-hidden="true" />
          Fees
          <ChevronRight size={14} aria-hidden="true" />
        </Link>
      </footer>
    </li>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'success' | 'warning' | 'danger';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-[color:var(--color-success)]'
      : tone === 'warning'
        ? 'text-[color:var(--color-warning)]'
        : 'text-[color:var(--color-danger)]';
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
      <p className="text-xs text-[color:var(--color-text-muted)]">{label}</p>
      <p className={`mt-0.5 font-display text-xl font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}
